import { CardContent, FormControl, Autocomplete, TextField, Tooltip, CardActions, Button } from '@mui/material';
import { v2_parcelSearchParameters, Permissions, CustomerInfo, DateFormats } from '@lanterds/types';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import React, { ChangeEvent, FormEvent, SyntheticEvent, useEffect, useState } from 'react';
import { getCustomersByPermission } from 'src/api/customer';
import LoadingIndicator from 'src/components/common/LoadingIndicator/LoadingIndicator';
import { ParcelSearchErrors } from 'src/util/constants';
import { extractParcelSearchInput, getUserEmailFromToken } from 'src/util/helpers';
import { isAuthorized } from 'src/util/Permissions';
import { queryParamsEnabled } from 'src/util/featureFlags';
import { DEFAULTS_PARAMS } from './types';
import * as Styled from './StyledComponents/FieldsContainer';

interface CallbacksShape {
    successCallback?: { (): void | undefined };
    errorCallback?: { (): void | undefined };
}

type FormProps = {
    isFetchingParcels: boolean;
    shouldCondenseForm: boolean;
    fetchParcels: (params: v2_parcelSearchParameters, callbacks?: CallbacksShape) => void;
    resetSearch: () => void;
    searchParams: v2_parcelSearchParameters;
};

interface FieldErrors {
    startDateError?: string;
    endDateError?: string;
    parcelCodeError?: string;
    manifestNumberError?: string;
}

export function ParcelSearchForm({ isFetchingParcels, fetchParcels, resetSearch, searchParams }: FormProps) {
    const loggedInUserEmail = getUserEmailFromToken();
    const extendedSearchAuthorized = isAuthorized(loggedInUserEmail, Permissions.parcelSearch_extendedSearch);
    const [formData, setFormData] = useState<v2_parcelSearchParameters>({});
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [selectedCustomer, setSelectedCustomer] = useState<{ label: string; id: string } | null>(null);
    const [customersAreLoading, setCustomersAreLoading] = useState(false);
    const [customers, setCustomers] = useState<Array<CustomerInfo>>([]);
    const [hasSetInputs, setHasSetInputs] = useState(false);

    useEffect(() => {
        fetchCustomers();
    }, [customers, fetchCustomers]);

    useEffect(() => {
        if (customers.length > 0 && !hasSetInputs) {
            // NOTE: we are only concerned about input parameters in this scenario
            const tmpParams = extractParcelSearchInput(searchParams);
            const hasPersistedFieldsInContext = Object.keys(tmpParams).length > 0;
            let persistedInputData: v2_parcelSearchParameters = hasPersistedFieldsInContext ? searchParams : {};

            // NOTE: prefer context values if they are present
            if (queryParamsEnabled && !hasPersistedFieldsInContext) {
                const queryParams = Object.fromEntries(new URLSearchParams(window.location.search));
                persistedInputData = queryParams;
            }

            if (persistedInputData?.customerId) {
                const customer = customers.find(
                    (customer: CustomerInfo) => customer.customerId === persistedInputData.customerId?.toString(),
                );
                if (customer) setSelectedCustomer({ label: customer.customerName, id: customer.customerId });
            }

            setFormData({ ...persistedInputData });
            setHasSetInputs(true);
        }
    }, [searchParams, hasSetInputs, customers]);

    async function fetchCustomers() {
        if (!customersAreLoading && customers.length === 0) {
            setCustomersAreLoading(true);
            getCustomersByPermission(Permissions.parcelSearch)
                .then((customers) => {
                    customers.sort((a: CustomerInfo, b: CustomerInfo) =>
                        a.customerName.toLocaleLowerCase().localeCompare(b.customerName.toLocaleLowerCase()),
                    );
                    setCustomers(customers);
                })
                .catch((error) => {
                    console.error(error);
                    throw error;
                })
                .finally(() => setCustomersAreLoading(false));
        }
    }

    const handleCustomerIdReset = () => {
        setSelectedCustomer(null);
        setFormData({
            ...formData,
            customerId: undefined,
        });
    };

    const validateForm = () => {
        const { startDate, endDate, parcelCode, manifestNumber } = formData;
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const lastDay = dayjs().subtract(7, 'day');
        let isValid = true;
        const newFieldErrors: FieldErrors = {};

        const commonFieldValidations = () => {
            if (!endDate && dayjs(startDate).isBefore(lastDay)) {
                newFieldErrors.startDateError = ParcelSearchErrors.noEndDate;
            }

            if (parcelCode && parcelCode.length < 4) {
                newFieldErrors.parcelCodeError = ParcelSearchErrors.requiredLength;
            }

            if (startDate && endDate && start > end) {
                newFieldErrors.startDateError = ParcelSearchErrors.invalidStartDate;
            }
        };

        if (!extendedSearchAuthorized) {
            if (!parcelCode && !manifestNumber) {
                newFieldErrors.parcelCodeError = ParcelSearchErrors.parcelOrManifestNum;
                newFieldErrors.manifestNumberError = ParcelSearchErrors.parcelOrManifestNum;
            } else {
                if (parcelCode && parcelCode.includes('%')) {
                    newFieldErrors.parcelCodeError = ParcelSearchErrors.invalidCharacter;
                }

                if (manifestNumber) {
                    if (manifestNumber.length < 4) {
                        newFieldErrors.manifestNumberError = ParcelSearchErrors.requiredLength;
                    }
                }

                commonFieldValidations();
            }
        } else {
            if ((!parcelCode || parcelCode.includes('%')) && !startDate) {
                if (!parcelCode) {
                    newFieldErrors.parcelCodeError = ParcelSearchErrors.startDateOrParcelNum;
                }

                if (!startDate) {
                    newFieldErrors.startDateError = ParcelSearchErrors.startDateOrParcelNum;
                }
            } else {
                commonFieldValidations();
            }
        }

        if (Object.keys(newFieldErrors).length) {
            setFieldErrors(newFieldErrors);
            isValid = false;
        }

        if (isValid) {
            setFieldErrors({});
        }

        return isValid;
    };

    const handleDatePickerChange = (name: string, value: Dayjs | null) => {
        setFormData({
            ...formData,
            [name]: value?.format(DateFormats.IsoDate),
        });
    };

    const handleCustomerChange = (event: SyntheticEvent<Element, Event>, value: { label: string; id: string } | null) => {
        setSelectedCustomer(value?.label && value?.id ? { label: value.label, id: value.id } : null);

        if (value && value?.id !== '') {
            const customer = customers.find(
                (customer: CustomerInfo) => customer.customerId.toLocaleLowerCase() === value.id.toLocaleLowerCase(),
            );

            if (customer && customer?.customerId) {
                setFormData({
                    ...formData,
                    customerId: customer.customerId,
                });
            }
        } else {
            setFormData({
                ...formData,
                customerId: undefined,
            });
        }
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleReset = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        (event.currentTarget as HTMLFormElement).reset();
        setFieldErrors({});
        setFormData({});
        setSelectedCustomer(null);
        resetSearch();

        if (queryParamsEnabled) {
            const updatedUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
            window.history.pushState({ path: updatedUrl }, '', updatedUrl);
        }
    };

    const handleSubmit = async (event: SyntheticEvent) => {
        event.preventDefault();

        const formIsValid = validateForm();
        if (formIsValid) {
            resetSearch();

            const tmpParams: v2_parcelSearchParameters = {
                ...formData,
                ...DEFAULTS_PARAMS,
            };

            if (queryParamsEnabled) {
                const queryParams = new URLSearchParams();
                const params = extractParcelSearchInput(tmpParams);

                Object.entries(params).forEach((entry) => {
                    const [key, value] = entry;
                    if (value) {
                        queryParams.set(key, value.toString());
                    }
                });

                const updatedUrl = `${window.location.protocol}//${window.location.host}${
                    window.location.pathname
                }?${queryParams.toString()}`;
                window.history.pushState({ path: updatedUrl }, '', updatedUrl);
            }

            fetchParcels(formData);
        }
    };

    return (
        <form onReset={handleReset} onSubmit={handleSubmit} autoComplete="off" style={{ width: '100%' }}>
            <CardContent>
                <Styled.FieldsContainer className="fields-container" extendedSearchAuthorized={extendedSearchAuthorized}>
                    {extendedSearchAuthorized && (
                        <FormControl fullWidth id="customerId-field">
                            <Autocomplete
                                id="customerId"
                                disablePortal
                                options={customers.map((customer) => ({
                                    label: customer.customerName,
                                    id: customer.customerId,
                                }))}
                                isOptionEqualToValue={(
                                    option: { label: string; id: string },
                                    value: { label: string; id: string },
                                ) => option.id === value.id}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label="Customer"
                                        name="customerId"
                                        InputProps={{
                                            ...params.InputProps,
                                        }}
                                        InputLabelProps={{ ...params.InputLabelProps }}
                                    />
                                )}
                                value={selectedCustomer ?? null}
                                onChange={handleCustomerChange}
                                disabled={customersAreLoading}
                                onReset={handleCustomerIdReset}
                            />
                        </FormControl>
                    )}

                    <FormControl fullWidth id="startDate-field">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                desktopModeMediaQuery="@media (min-width: 400px)"
                                name="startDate"
                                value={formData.startDate ? dayjs(formData.startDate) : null}
                                onChange={(value) => handleDatePickerChange('startDate', value)}
                                slotProps={{
                                    field: { clearable: true },
                                    actionBar: {
                                        actions: ['clear', 'today'],
                                        sx: { justifyContent: 'space-between' },
                                    },
                                    textField: {
                                        id: 'startDate',
                                        variant: 'outlined',
                                        placeholder: 'MM/DD/YYYY',
                                        label: 'Start Date',
                                        error: !!fieldErrors?.startDateError,
                                        helperText: fieldErrors?.startDateError,
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </FormControl>

                    <FormControl fullWidth id="endDate-field">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                desktopModeMediaQuery="@media (min-width: 400px)"
                                name="endDate"
                                value={formData.endDate ? dayjs(formData.endDate) : null}
                                onChange={(value) => handleDatePickerChange('endDate', value)}
                                slotProps={{
                                    field: { clearable: true },
                                    actionBar: {
                                        actions: ['clear', 'today'],
                                        sx: { justifyContent: 'space-between' },
                                    },
                                    textField: {
                                        id: 'endDate',
                                        variant: 'outlined',
                                        placeholder: 'MM/DD/YYYY',
                                        label: 'End Date',
                                        error: !!fieldErrors?.endDateError,
                                        helperText: fieldErrors?.endDateError,
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </FormControl>

                    {extendedSearchAuthorized && (
                        <FormControl fullWidth id="endpoint-field">
                            <TextField
                                id="endpoint"
                                variant="outlined"
                                label="End Point No."
                                name="endpoint"
                                value={formData.endpoint ?? ''}
                                onChange={handleInputChange}
                            />
                        </FormControl>
                    )}

                    <FormControl fullWidth id="manifestNumber-field">
                        <TextField
                            id="manifestNumber"
                            variant="outlined"
                            label="Manifest Number"
                            name="manifestNumber"
                            value={formData.manifestNumber ?? ''}
                            onChange={handleInputChange}
                            error={!!fieldErrors?.manifestNumberError}
                            helperText={fieldErrors?.manifestNumberError}
                        />
                    </FormControl>

                    {extendedSearchAuthorized && (
                        <FormControl fullWidth id="partNumber-field">
                            <TextField
                                id="partNumber"
                                variant="outlined"
                                label="Part Number"
                                name="partNumber"
                                value={formData.partNumber ?? ''}
                                onChange={handleInputChange}
                            />
                        </FormControl>
                    )}

                    <FormControl fullWidth className="parcelCode-field">
                        <Tooltip
                            arrow
                            title={extendedSearchAuthorized ? 'Use the percent (%) character as a search wild card' : ''}
                            placement="top"
                        >
                            <TextField
                                id="parcelCode"
                                variant="outlined"
                                label="Parcel Number"
                                name="parcelCode"
                                value={formData.parcelCode ?? ''}
                                onChange={handleInputChange}
                                InputProps={{
                                    sx: { borderRadius: '10px' },
                                }}
                                error={!!fieldErrors?.parcelCodeError}
                                helperText={fieldErrors?.parcelCodeError}
                            />
                        </Tooltip>
                    </FormControl>

                    <CardActions disableSpacing={true} className="buttons-container">
                        <Button
                            variant="outlined"
                            type="reset"
                            className="clear-button"
                            sx={{ height: '56px', width: '100px' }}
                        >
                            Clear
                        </Button>
                        {isFetchingParcels ? (
                            <Button
                                variant="contained"
                                type="submit"
                                disabled
                                className="submit-button"
                                sx={{ height: '56px', width: '100px', marginLeft: '10px' }}
                            >
                                <LoadingIndicator message="Loading" />
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                type="submit"
                                className="submit-button"
                                sx={{ height: '56px', width: '100px', marginLeft: '10px' }}
                            >
                                Search
                            </Button>
                        )}
                    </CardActions>
                </Styled.FieldsContainer>
            </CardContent>
        </form>
    );
}
